import React from "react";
import { ArrowLeft, Loader2, AlertTriangle, ShoppingCart, Cloud, CheckCircle2 } from "lucide-react";
import { LARGE_BATCH, btnPrimary, btnGhost, registrarLabel } from "./onboardShared";
import { ReviewConfigCard } from "./ReviewConfigCard";

function PlanLine({ w }) {
  const { plan, buyMode } = w;
  if (!buyMode) return <>Plan: <b className="text-emerald-400">{plan.new_count}</b> new · {plan.skip_count} already onboarded (skipped)</>;
  return (
    <>Plan: <b className="text-zinc-200">{plan.buyable?.length}</b> buyable · total <span className="mono text-amber-300">${plan.total_usd?.toFixed(2)}</span> · {plan.balance ? <>within budget: {plan.within_budget ? "yes" : "no"}</> : "billed to Cloudflare card"}{plan.unavailable?.length ? ` · unavailable: ${plan.unavailable.join(", ")}` : ""}</>
  );
}

function ReviewSummaryCard({ w }) {
  const { buyMode, cfBuy, selectedList, selectedTotal, balance, plan } = w;
  const results = w.buy.results;
  return (
    <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-5">
      <div className="label-tiny mb-2">{buyMode ? `Buying via ${registrarLabel(w.buy.registrar)}` : "Onboarding"} · {selectedList.length} domain(s)</div>
      <div className="divide-y divide-zinc-800 max-h-52 overflow-y-auto border border-zinc-800 rounded-sm">
        {selectedList.map((d) => {
          const row = results?.find((r) => r.domain === d);
          const price = row?.total_usd ?? row?.price_usd;
          return (
            <div key={d} className="flex items-center justify-between px-3 py-1.5 text-xs">
              <span className="mono text-zinc-200 truncate">{d}{row?.min_years > 1 && <span className="ml-1.5 text-amber-300">({row.min_years}yr min)</span>}</span>
              {buyMode && <span className="mono text-zinc-400">${(price || 0).toFixed(2)}</span>}
            </div>
          );
        })}
      </div>
      {buyMode && (
        <>
          <div className="flex items-center justify-between mt-3 text-sm"><span className="text-zinc-400">Estimated total</span><span className="mono font-bold text-amber-300">${selectedTotal.toFixed(2)}</span></div>
          {!cfBuy && balance != null && <div className={`mt-1 text-xs mono ${selectedTotal > balance ? "text-rose-400" : "text-emerald-400"}`}>Balance ${balance.toFixed(2)} — {selectedTotal > balance ? "insufficient" : "sufficient"}</div>}
          {cfBuy && <div data-testid="domains-cf-billing-note" className="mt-1 text-xs mono text-sky-300">Charged to your Cloudflare account's default card · non-refundable · auto-renew off · WHOIS redacted</div>}
        </>
      )}
      {plan && <div data-testid="domains-plan-out" className="mt-3 text-xs border border-zinc-800 rounded-sm p-3 text-zinc-400"><PlanLine w={w} /></div>}
    </div>
  );
}

function GoIcon({ buyMode, cfBuy }) {
  if (!buyMode) return <CheckCircle2 size={15} />;
  return cfBuy ? <Cloud size={15} /> : <ShoppingCart size={15} />;
}

function goLabel(buyMode, cfBuy) {
  if (!buyMode) return "Onboard";
  return cfBuy ? "Buy via Cloudflare & onboard" : "Buy & onboard";
}

/** Review phase: config + summary cards, large-batch confirm, dry-run and go. */
export function ReviewPhase({ w }) {
  const { buyMode, cfBuy, selectedList, selectedTotal, busy, confirmLarge, setConfirmLarge } = w;
  const large = buyMode && selectedList.length > LARGE_BATCH;
  return (
    <div className="mt-5 space-y-5">
      <div className="grid md:grid-cols-2 gap-4">
        <ReviewConfigCard w={w} />
        <ReviewSummaryCard w={w} />
      </div>
      <div className="flex items-center gap-2">
        <button data-testid="domains-back-btn" onClick={() => w.setPhase("collect")} className={btnGhost}><ArrowLeft size={15} /> Back</button>
        <div className="ml-auto flex items-center gap-3">
          {large && (
            <label data-testid="domains-large-confirm" className="flex items-center gap-2 text-xs text-amber-300 cursor-pointer">
              <input type="checkbox" checked={confirmLarge} onChange={(e) => setConfirmLarge(e.target.checked)} />
              <AlertTriangle size={13} /> Confirm large buy: {selectedList.length} domains · ${selectedTotal.toFixed(2)}
            </label>
          )}
          <button data-testid="domains-dryrun-btn" onClick={w.doDryRun} disabled={busy} className={btnGhost}>{busy ? <Loader2 size={15} className="animate-spin" /> : null} Dry-run preview</button>
          <button data-testid="domains-buy-btn" onClick={w.doGo} disabled={busy || selectedList.length === 0 || (large && !confirmLarge)} className={btnPrimary}>
            {busy ? <Loader2 size={15} className="animate-spin" /> : <GoIcon buyMode={buyMode} cfBuy={cfBuy} />} {goLabel(buyMode, cfBuy)} {selectedList.length}
          </button>
        </div>
      </div>
    </div>
  );
}
